import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../../../app/store'

export type ProrithmEcgEntry = {
  e: number
  t: number
  o: number
}

export type DeviceState = {
  prorithm: Record<string, any>
  prorithmEcg: ProrithmEcgEntry[]
  safeRanges: Record<string, any>
  lastUpdated: string | null
  isLoading: boolean
}

const initialState: DeviceState = {
  prorithm: {},
  prorithmEcg: [],
  safeRanges: {
    heartRate: { min: 60, max: 100 },
    spo2: { min: 95, max: 100 },
    bloodPressureSystolic: { min: 90, max: 140 },
    bloodPressureDiastolic: { min: 90, max: 140 },
    skinTemperature: { min: 36, max: 37.5 },
    respiratoryRate: { min: 12, max: 20 },
    heartRateVar: { min: 30, max: 60 },
    stress: { min: 0, max: 40 },
    sleep: { min: 6, max: 8 },
    bloodGlucose: { min: 0, max: 200 },
  },
  lastUpdated: null,
  isLoading: false,
}

const DeviceSlice = createSlice({
  name: 'Device',
  initialState,
  reducers: {
    setProrithm: (state, action: PayloadAction<Record<string, any>>) => {
      state.prorithm = action.payload
      state.lastUpdated = new Date().toISOString()
    },
    setProrithmEcg: (state, action: PayloadAction<ProrithmEcgEntry[]>) => {
      state.prorithmEcg = action.payload
      state.lastUpdated = new Date().toISOString()
    },
    setSafeRanges: (state, action: PayloadAction<Record<string, any>>) => {
      state.safeRanges = action.payload
      state.lastUpdated = new Date().toISOString()
    },
    updateProrithm: (state, action: PayloadAction<Record<string, any>>) => {
      state.prorithm = { ...state.prorithm, ...action.payload }
      state.lastUpdated = new Date().toISOString()
    },
    updateProrithmEcg: (state, action: PayloadAction<ProrithmEcgEntry[]>) => {
      state.prorithmEcg = [...state.prorithmEcg, ...action.payload]
      state.lastUpdated = new Date().toISOString()
    },
    updateSafeRanges: (state, action: PayloadAction<Record<string, any>>) => {
      state.safeRanges = { ...state.safeRanges, ...action.payload }
      state.lastUpdated = new Date().toISOString()
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
})

export const {
  setProrithm,
  setProrithmEcg,
  setSafeRanges,
  updateProrithm,
  updateProrithmEcg,
  updateSafeRanges,
  setLoading,
} = DeviceSlice.actions

export const selectProrithm = (state: RootState) => state?.Device?.prorithm
export const selectProrithmEcg = (state: RootState) =>
  state?.Device?.prorithmEcg
export const selectSafeRanges = (state: RootState) => state?.Device?.safeRanges
export const selectIsLoading = (state: RootState) => state?.Device?.isLoading
export const selectLastUpdated = (state: RootState) =>
  state?.Device?.lastUpdated

export default DeviceSlice.reducer
